import { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ModelTrainingRoundedIcon from "@mui/icons-material/ModelTrainingRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import { formatParameterCount } from "./ModelFilterPanel";
import ModelCardMarkdownPreview from "./ModelCardMarkdownPreview";
import { getPipelineColor } from "./pipelineCategory";
import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  HAIRLINE_STRONG,
  ICON,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  WEIGHT,
  withAlpha,
} from "../../../theme/tokens";

const TAG_PREVIEW_COUNT = 8;

export function buildHuggingFaceUrl(repoId) {
  return `https://huggingface.co/${repoId}`;
}

export function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1,
  );
  const scaled = value / 1024 ** index;
  const precision = scaled >= 10 || index === 0 ? 0 : 1;
  return `${scaled.toFixed(precision)} ${units[index]}`;
}

function formatTimestamp(seconds) {
  if (seconds == null) return "Unknown";
  const date = new Date(Number(seconds) * 1000);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function relativeTimestamp(seconds) {
  if (seconds == null) return "Updated unknown";
  const millis = Number(seconds) * 1000;
  if (!Number.isFinite(millis)) return "Updated unknown";

  const diff = Date.now() - millis;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) return "Updated less than an hour ago";
  if (diff < day) return `Updated ${Math.max(1, Math.round(diff / hour))}h ago`;
  if (diff < 30 * day) {
    return `Updated ${Math.max(1, Math.round(diff / day))} days ago`;
  }
  return `Updated ${formatTimestamp(seconds)}`;
}

export function bestRevision(model) {
  const revisions = Array.isArray(model?.revisions) ? model.revisions : [];
  return (
    revisions.find((revision) => revision.refs?.includes("main")) ||
    revisions[0] ||
    null
  );
}

export function getParameterCount(model) {
  const value = Number(model?.parameter_count || 0);
  return Number.isFinite(value) ? value : 0;
}

function ModelMetaChip({ icon, label, color }) {
  const tinted = Boolean(color);
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      variant={tinted ? "filled" : "outlined"}
      sx={{
        height: 24,
        borderColor: tinted ? "transparent" : HAIRLINE,
        color: tinted ? color : INK_SECONDARY,
        bgcolor: tinted ? withAlpha(color, 0.12) : SURFACE,
        fontSize: FONT.xs,
        fontWeight: tinted ? WEIGHT.semibold : WEIGHT.regular,
        "& .MuiChip-icon": { color: tinted ? color : ICON, ml: 0.75 },
      }}
    />
  );
}

ModelMetaChip.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.node.isRequired,
  color: PropTypes.string,
};

function DetailField({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <Box>
      <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: FONT.sm, color: INK, overflowWrap: "anywhere" }}
      >
        {String(value)}
      </Typography>
    </Box>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};

function DrawerSection({ label, icon, children }) {
  return (
    <Box
      sx={{
        border: `1px solid ${HAIRLINE}`,
        borderRadius: `${RADIUS.md}px`,
        bgcolor: SURFACE,
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, mb: 1.5 }}>
        {icon}
        <Typography
          sx={{
            fontSize: FONT.md,
            fontWeight: WEIGHT.bold,
            color: INK,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

DrawerSection.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export function ModelDetails({ model, detail, loading, error }) {
  const [showAllTags, setShowAllTags] = useState(false);
  const hub = detail?.hub;
  const revisions = model?.revisions || [];
  const repoId = model?.repo_id || "";
  const parameterCount = hub?.parameter_count || getParameterCount(model);
  const tags = hub?.tags || [];
  const visibleTags = showAllTags ? tags : tags.slice(0, TAG_PREVIEW_COUNT);
  const hiddenTagCount = tags.length - visibleTags.length;
  const isCardTruncated =
    typeof detail?.model_card === "string" &&
    detail.model_card.trim().endsWith("...");

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton height={22} width="45%" sx={{ mb: 1 }} />
        <Stack direction="row" gap={0.75} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" height={24} width={90} />
          <Skeleton variant="rounded" height={24} width={90} />
          <Skeleton variant="rounded" height={24} width={90} />
        </Stack>
        <Skeleton height={18} width="95%" />
        <Skeleton height={18} width="90%" />
        <Skeleton height={18} width="70%" />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && hub?.error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hub metadata (pipeline, library, downloads, likes) could not be
          fetched from Hugging Face right now, so some fields below may be
          missing. Cache and revision info is still accurate. ({hub.error})
        </Alert>
      )}

      <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
        {parameterCount > 0 && (
          <ModelMetaChip
            icon={<LayersRoundedIcon sx={{ fontSize: 14 }} />}
            label={`${formatParameterCount(parameterCount)} params`}
          />
        )}
        <ModelMetaChip
          icon={<StorageRoundedIcon sx={{ fontSize: 14 }} />}
          label={formatBytes(model?.size_on_disk)}
        />
        {hub?.downloads != null && (
          <ModelMetaChip
            icon={<DownloadRoundedIcon sx={{ fontSize: 14 }} />}
            label={`${hub.downloads.toLocaleString()} downloads`}
          />
        )}
        {hub?.likes != null && (
          <ModelMetaChip
            icon={<FavoriteBorderRoundedIcon sx={{ fontSize: 14 }} />}
            label={`${hub.likes.toLocaleString()} likes`}
          />
        )}
      </Stack>

      <Stack gap={2}>
        {tags.length > 0 && (
          <DrawerSection
            label="Tags"
            icon={<SellOutlinedIcon sx={{ fontSize: 19, color: ACCENT }} />}
          >
            <Stack direction="row" gap={0.6} flexWrap="wrap">
              {visibleTags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  variant="outlined"
                  label={tag}
                  sx={{
                    height: 22,
                    borderColor: HAIRLINE,
                    color: INK_MUTED,
                    fontSize: FONT.xs,
                  }}
                />
              ))}
              {hiddenTagCount > 0 && (
                <Chip
                  size="small"
                  clickable
                  variant="outlined"
                  onClick={() => setShowAllTags(true)}
                  label={`+${hiddenTagCount} more`}
                  sx={{
                    height: 22,
                    borderColor: HAIRLINE,
                    color: ACCENT,
                    fontWeight: WEIGHT.semibold,
                    fontSize: FONT.xs,
                  }}
                />
              )}
              {showAllTags && tags.length > TAG_PREVIEW_COUNT && (
                <Chip
                  size="small"
                  clickable
                  variant="outlined"
                  onClick={() => setShowAllTags(false)}
                  label="Show less"
                  sx={{
                    height: 22,
                    borderColor: HAIRLINE,
                    color: INK_SECONDARY,
                    fontSize: FONT.xs,
                  }}
                />
              )}
            </Stack>
          </DrawerSection>
        )}

        <DrawerSection
          label="Introduction"
          icon={
            <DescriptionOutlinedIcon sx={{ fontSize: 19, color: ACCENT }} />
          }
        >
          <ModelCardMarkdownPreview content={detail?.model_card} />
          {isCardTruncated && (
            <Typography
              sx={{
                fontSize: FONT.xs,
                color: INK_MUTED,
                fontStyle: "italic",
                mt: 1,
              }}
            >
              Preview limited to the first 500 characters - see the full model
              card on{" "}
              <Link
                href={buildHuggingFaceUrl(repoId)}
                target="_blank"
                rel="noreferrer"
                sx={{ color: ACCENT }}
              >
                Hugging Face
              </Link>
              .
            </Typography>
          )}
        </DrawerSection>

        <DrawerSection
          label="Metadata"
          icon={<DataObjectRoundedIcon sx={{ fontSize: 19, color: ACCENT }} />}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.25,
              mb: revisions.length ? 1.5 : 0,
            }}
          >
            <DetailField label="Local cache path" value={model?.local_path} />
            <DetailField label="Hub commit SHA" value={hub?.sha} />
          </Box>

          {revisions.length > 0 && (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {revisions.map((revision) => (
                <Chip
                  key={revision.commit_hash}
                  size="small"
                  label={`${revision.refs?.join(", ") || "commit"} - ${revision.commit_hash?.slice(0, 8) || "unknown"} - ${formatBytes(revision.size_on_disk)}`}
                  variant="outlined"
                  sx={{
                    maxWidth: "100%",
                    borderColor: HAIRLINE,
                    fontSize: FONT.xs,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              ))}
            </Stack>
          )}
        </DrawerSection>
      </Stack>
    </Box>
  );
}

ModelDetails.propTypes = {
  model: PropTypes.object.isRequired,
  detail: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default function ModelCard({
  model,
  active,
  onOpenDetails,
  loadingDetail,
  is_admin_page,
  onDelete,
  deleting,
}) {
  const revision = bestRevision(model);
  const repoId = model?.repo_id || "Unknown model";
  const revisionCount = model?.revisions?.length || 0;
  const parameterCount = getParameterCount(model);
  const pipelineTag = model?.pipeline_tag;
  const pipelineColor = getPipelineColor(pipelineTag);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        borderColor: active ? ACCENT : HAIRLINE,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: `${RADIUS.sm}px`,
            display: "grid",
            placeItems: "center",
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
          }}
        >
          <ModelTrainingRoundedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Link
          href={buildHuggingFaceUrl(repoId)}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          title={repoId}
          sx={{
            fontSize: FONT.lg,
            fontWeight: WEIGHT.semibold,
            color: INK,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            "&:hover": { color: ACCENT },
          }}
        >
          {repoId}
        </Link>
        <OpenInNewRoundedIcon
          sx={{ color: INK_MUTED, fontSize: 16, flexShrink: 0 }}
        />
      </Box>

      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {pipelineTag && (
          <ModelMetaChip
            color={pipelineColor}
            icon={<TuneRoundedIcon sx={{ fontSize: 14 }} />}
            label={pipelineTag}
          />
        )}
        {parameterCount > 0 && (
          <ModelMetaChip
            icon={<LayersRoundedIcon sx={{ fontSize: 14 }} />}
            label={`${formatParameterCount(parameterCount)} params`}
          />
        )}
        <ModelMetaChip
          icon={<StorageRoundedIcon sx={{ fontSize: 14 }} />}
          label={formatBytes(model?.size_on_disk)}
        />
      </Stack>

      <Typography
        sx={{
          fontSize: FONT.sm,
          color: INK_SECONDARY,
          lineHeight: 1.55,
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.6,
        }}
      >
        <AccountTreeRoundedIcon sx={{ fontSize: 15, color: INK_MUTED }} />
        {`${revisionCount} revision${revisionCount === 1 ? "" : "s"}`}
        <Box component="span" sx={{ color: HAIRLINE_STRONG }}>
          -
        </Box>
        {relativeTimestamp(revision?.last_modified ?? model?.last_modified)}
      </Typography>

      <Box
        sx={{
          mt: "auto",
          pt: 1.25,
          borderTop: `1px solid ${HAIRLINE}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={() => onOpenDetails(repoId)}
          startIcon={
            loadingDetail ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            textTransform: "none",
            fontSize: FONT.sm,
            fontWeight: 600,
            color: ACCENT,
            px: 0.75,
            ml: -0.75,
            "&:hover": { backgroundColor: ACCENT_TINT },
          }}
        >
          View details
        </Button>
        {is_admin_page && (
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={deleting}
            onClick={() => onDelete(repoId)}
            startIcon={
              deleting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              ml: "auto",
              position: "sticky",
              right: 0,
              textTransform: "none",
              fontSize: FONT.sm,
              fontWeight: WEIGHT.semibold,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Delete
          </Button>
        )}
      </Box>
    </Card>
  );
}

ModelCard.propTypes = {
  model: PropTypes.object.isRequired,
  active: PropTypes.bool,
  onOpenDetails: PropTypes.func.isRequired,
  loadingDetail: PropTypes.bool,
  is_admin_page: PropTypes.bool,
  onDelete: PropTypes.func,
  deleting: PropTypes.bool,
};

ModelCard.defaultProps = {
  is_admin_page: false,
  onDelete: () => {},
  deleting: false,
};
